import React, { useState, useEffect, useCallback } from 'react';
import CommentThread from './CommentThread';
import MarkdownFieldSimple from '../MarkdownFieldSimple';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL, fetchWithAuth } from '../../services/ApiService';
import './Comments.css';

const CommentsSection = ({ 
  objectType, 
  objectId, 
  collapsed = false,
  maxDepth = 3,
  printMode = false,
  allowComments = true 
}) => {
  const { currentUser, isAuthenticated } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  });

  // Fetch comments
  const fetchComments = useCallback(async (offset = 0) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/comments/${objectType}/${objectId}?limit=50&offset=${offset}&threaded=true`,
        {
          credentials: 'include'
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch comments');
      }
      
      const data = await response.json();
      
      if (offset === 0) {
        setComments(data.comments);
      } else {
        setComments(prev => [...prev, ...data.comments]);
      }
      
      setPagination(data.pagination);
      setError(null);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [objectType, objectId]);

  useEffect(() => {
    if (objectType && objectId) {
      fetchComments(0);
    }
  }, [objectType, objectId, fetchComments]);

  // Handle comment submission
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim() || !isAuthenticated) {
      return;
    }
    
    try {
      setSubmitting(true);
      
      // For now, send empty mentions array since mentions are embedded in content
      // Backend expects user IDs, not usernames
      
      const newCommentData = await fetchWithAuth(`${API_URL}/comments`, {
        method: 'POST',
        body: JSON.stringify({
          object_type: objectType,
          object_id: objectId,
          content: newComment,
          mentions: []
        })
      });
      
      // Add the new comment to the list
      setComments(prev => [newCommentData, ...prev]);
      
      // Clear the form
      setNewComment('');
      
    } catch (err) {
      console.error('Error posting comment:', err);
      console.error('Error details:', err.message);
      alert(`Failed to post comment: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle reply to comment
  const handleReply = async (parentId, content) => {
    if (!content.trim() || !isAuthenticated) {
      return;
    }
    
    try {
      // For now, send empty mentions array since mentions are embedded in content
      // Backend expects user IDs, not usernames
      
      await fetchWithAuth(`${API_URL}/comments`, {
        method: 'POST',
        body: JSON.stringify({
          object_type: objectType,
          object_id: objectId,
          parent_id: parentId,
          content: content,
          mentions: []
        })
      });
      
      // Refresh comments to show the new reply
      await fetchComments(0);
      
    } catch (err) {
      console.error('Error posting reply:', err);
      throw err;
    }
  };

  // Handle comment edit
  const handleEdit = async (commentId, newContent) => {
    try {
      // For now, send empty mentions array since mentions are embedded in content
      // Backend expects user IDs, not usernames
      
      await fetchWithAuth(`${API_URL}/comments/${commentId}`, {
        method: 'PUT',
        body: JSON.stringify({
          content: newContent,
          mentions: []
        })
      });
      
      // Refresh comments to show the edit
      await fetchComments(0);
      
    } catch (err) {
      console.error('Error editing comment:', err);
      throw err;
    }
  };

  // Handle comment delete
  const handleDelete = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }
    
    try {
      await fetchWithAuth(`${API_URL}/comments/${commentId}`, {
        method: 'DELETE'
      });
      
      // Refresh comments to remove the deleted one
      await fetchComments(0);
      
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment. Please try again.');
    }
  };

  // Load more comments
  const handleLoadMore = () => {
    fetchComments(pagination.offset + pagination.limit);
  };

  // Render for print mode
  if (printMode) {
    return (
      <div className="comments-section-print">
        <h3>Comments ({pagination.total})</h3>
        {comments.map(comment => (
          <CommentThread
            key={comment._id}
            comment={comment}
            onReply={() => {}}
            onEdit={() => {}}
            onDelete={() => {}}
            maxDepth={maxDepth}
            currentDepth={0}
            printMode={true}
            user={currentUser}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="comments-section">
      <div className="comments-header">
        <h3 onClick={() => setIsCollapsed(!isCollapsed)} className="comments-title">
          <span className="collapse-icon">{isCollapsed ? '▶' : '▼'}</span>
          Comments ({pagination.total})
        </h3>
      </div>
      
      {!isCollapsed && (
        <div className="comments-content">
          {/* New comment form */}
          {isAuthenticated && allowComments && !printMode && (
            <form onSubmit={handleSubmitComment} className="new-comment-form">
              <MarkdownFieldSimple
                type="textarea"
                rows={3}
                value={newComment}
                onChange={setNewComment}
                placeholder="Write a comment... (@ to mention, Cmd+K for links, Markdown supported)"
                disabled={submitting}
              />
              <div className="comment-form-actions">
                <button 
                  type="submit" 
                  disabled={!newComment.trim() || submitting}
                  className="btn-primary"
                >
                  {submitting ? 'Posting...' : 'Post Comment'}
                </button>
              </div>
            </form>
          )}
          
          {/* Comments list */}
          {loading && comments.length === 0 ? (
            <div className="comments-loading">Loading comments...</div>
          ) : error ? (
            <div className="comments-error">{error}</div>
          ) : comments.length === 0 ? (
            <div className="comments-empty">
              No comments yet. {isAuthenticated && 'Be the first to comment!'}
            </div>
          ) : (
            <>
              <div className="comments-list">
                {comments.map(comment => (
                  <CommentThread
                    key={comment._id}
                    comment={comment}
                    onReply={handleReply}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    maxDepth={maxDepth}
                    currentDepth={0}
                    printMode={false}
                    user={currentUser}
                  />
                ))}
              </div>
              
              {/* Load more button */}
              {pagination.hasMore && (
                <div className="comments-load-more">
                  <button 
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="btn-secondary"
                  >
                    {loading ? 'Loading...' : 'Load More Comments'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentsSection;